/**
 * 📂 考卷紀錄 — 拍照上傳批改過的考卷都存在這裡(答對的也不浪費)。
 *
 * 流程設計(boss 2026-06-10「考卷沒錯的地方就沒了」):
 *   拍照上傳 → 批改確認 → 錯題進錯題本(原題重練)
 *                        → 全部題升降熟練度
 *                        → 整卷歸檔到這裡 → 隨時 🔁 出變形練習卷(每次數字/情境都不同)
 *                                         → 📄 看題目(含重繪圖表、對錯標記、正解)
 *
 * 視覺 = 學術風格設計系統(pageShell / ef token)。
 * 學習介面要專業:無音效、無 mascot、文案中性。
 * 鐵則:純學習側;變形卷批改入帳在 variant-practice 內,本畫面只讀資料。
 */

import { loadProgress } from '../../shared/progress';
import type { ArchivedExam } from '../../shared/types';
import { archivedToExtracted, examStats } from '../exam-upload/archive';
import { normalizeFigure, renderFigure } from '../exam-upload/figure';
import { runVariantPractice } from './variant-practice';
import { pageShell, academicButton } from '../ui/components';

const SUBJECT_LABEL: Record<string, string> = {
  math: '數學',
  chinese: '國語',
  english: '英語',
  nature: '自然',
  social: '社會',
};

/** '2026-06-10T03:21:00Z' → '2026/6/10'(壞字串退回原樣)。 */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

/** 一張卷涵蓋的科目標籤(去重、按出現順序)。 */
function subjectLabels(exam: ArchivedExam): string {
  const seen: string[] = [];
  for (const it of exam.items) {
    const label = SUBJECT_LABEL[it.subject] ?? it.subject;
    if (!seen.includes(label)) seen.push(label);
  }
  return seen.join('・');
}

const DECISION_BADGE: Record<string, { text: string; cls: string }> = {
  correct: { text: '✓ 對', cls: 'ea-badge--correct' },
  wrong: { text: '✗ 錯', cls: 'ea-badge--wrong' },
  careless: { text: '△ 粗心', cls: 'ea-badge--careless' },
};

/** 考卷紀錄主畫面。onDone = 返回學習堡壘。 */
export function renderExamArchive(container: HTMLElement, onDone: () => void): void {
  ensureArchiveStyle();
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: '考卷紀錄',
    subtitle: '上傳批改過的考卷都存在這裡 — 答對的也不浪費,隨時出變形卷再練一次概念。',
    onBack: onDone,
  });

  const progress = loadProgress();
  const exams = progress?.uploadedExams ?? [];

  const root = document.createElement('div');
  root.className = 'exam-archive';
  root.setAttribute('data-testid', 'exam-archive-screen');
  shell.body.appendChild(root);

  if (exams.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'ea-empty';
    empty.textContent = '還沒有歸檔的考卷。到「拍照上傳」拍一張批改完,整張卷就會自動存進來。';
    root.appendChild(empty);
    return;
  }

  for (const exam of exams) {
    root.appendChild(renderExamCard(container, exam, onDone));
  }
}

/** 一張卷的卡片:摘要列 + 出變形卷 / 看題目。 */
function renderExamCard(screenRoot: HTMLElement, exam: ArchivedExam, onDone: () => void): HTMLElement {
  const card = document.createElement('div');
  card.className = 'ea-card ef-card';
  card.setAttribute('data-testid', 'exam-archive-card');

  const stats = examStats(exam);
  const head = document.createElement('div');
  head.className = 'ea-head';
  const title = document.createElement('div');
  title.className = 'ea-title';
  title.textContent = `${formatDate(exam.dateISO)} · ${subjectLabels(exam)}`;
  const meta = document.createElement('div');
  meta.className = 'ea-meta';
  meta.textContent = `共 ${stats.total} 題 · 答對 ${stats.correct} · 答錯 ${stats.wrong}`;
  head.append(title, meta);
  card.appendChild(head);

  const actions = document.createElement('div');
  actions.className = 'ea-actions';

  const variantBtn = academicButton({
    label: '🔁 出變形練習卷',
    variant: 'primary',
    onClick: () => {
      screenRoot.innerHTML = '';
      void runVariantPractice(screenRoot, archivedToExtracted(exam.items), () =>
        renderExamArchive(screenRoot, onDone),
      );
    },
  });
  variantBtn.setAttribute('data-testid', 'exam-archive-variant');
  actions.appendChild(variantBtn);

  const detail = document.createElement('div');
  detail.className = 'ea-detail';
  detail.hidden = true;

  const toggleBtn = academicButton({
    label: '📄 看題目',
    variant: 'secondary',
    onClick: () => {
      if (detail.hidden && detail.childElementCount === 0) {
        renderItems(detail, exam); // 第一次展開才 render(圖表 SVG 不白做)
      }
      detail.hidden = !detail.hidden;
    },
  });
  toggleBtn.setAttribute('data-testid', 'exam-archive-toggle');
  actions.appendChild(toggleBtn);

  card.appendChild(actions);
  card.appendChild(detail);
  return card;
}

/** 展開的題目清單:對錯標記 + 題幹 + 重繪圖表 + 正解/解析。 */
function renderItems(host: HTMLElement, exam: ArchivedExam): void {
  exam.items.forEach((it, idx) => {
    const row = document.createElement('div');
    row.className = 'ea-item';

    const line = document.createElement('div');
    line.className = 'ea-item-line';
    const badge = DECISION_BADGE[it.decision] ?? DECISION_BADGE.wrong;
    const badgeEl = document.createElement('span');
    badgeEl.className = `ea-badge ${badge.cls}`;
    badgeEl.textContent = badge.text;
    const prompt = document.createElement('span');
    prompt.className = 'ea-prompt';
    prompt.textContent = `${idx + 1}. ${it.prompt}`;
    line.append(badgeEl, prompt);
    row.appendChild(line);

    const fig = normalizeFigure(it.figure);
    if (fig) {
      try {
        row.appendChild(renderFigure(fig));
      } catch {
        /* 圖壞掉不影響題目 */
      }
    }

    const answer = document.createElement('div');
    answer.className = 'ea-answer';
    const correct =
      it.kind === 'mcq' && typeof it.correctAnswer === 'number'
        ? String(it.options?.[it.correctAnswer] ?? it.correctAnswer)
        : String(it.correctAnswer);
    answer.textContent = `正解:${correct}${it.explanation ? ` —— ${it.explanation}` : ''}`;
    row.appendChild(answer);

    host.appendChild(row);
  });
}

const ARCHIVE_STYLE_ID = 'ef-exam-archive-style';
function ensureArchiveStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(ARCHIVE_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = ARCHIVE_STYLE_ID;
  style.textContent = `
.exam-archive { display:flex; flex-direction:column; gap:14px; font-family:var(--ef-sans); color:var(--ef-ink-soft); }
.exam-archive .ea-empty { margin:0; font-size:15px; line-height:1.7; color:var(--ef-sub); }
.exam-archive .ea-card { display:flex; flex-direction:column; gap:12px; cursor:default; }
.exam-archive .ea-card:hover { transform:none; box-shadow:var(--ef-shadow-card); border-color:var(--ef-line); }
.exam-archive .ea-head { display:flex; flex-direction:column; gap:3px; }
.exam-archive .ea-title { font-family:var(--ef-serif); font-weight:700; font-size:17px; color:var(--ef-ink); }
.exam-archive .ea-meta { font-size:13px; color:var(--ef-sub); }
.exam-archive .ea-actions { display:flex; gap:8px; flex-wrap:wrap; }
.exam-archive .ea-detail { display:flex; flex-direction:column; gap:12px; padding-top:10px; border-top:1px solid var(--ef-line); }
.exam-archive .ea-item { display:flex; flex-direction:column; gap:6px; }
.exam-archive .ea-item-line { display:flex; gap:8px; align-items:baseline; }
.exam-archive .ea-badge { flex:0 0 auto; font-size:12px; font-weight:700; padding:1px 8px; border-radius:var(--ef-radius-pill); border:1px solid var(--ef-line); }
.exam-archive .ea-badge--correct { color:var(--ef-green-dark); border-color:var(--ef-green); }
.exam-archive .ea-badge--wrong { color:#b3261e; border-color:#d9a09b; }
.exam-archive .ea-badge--careless { color:#8a6d1a; border-color:#d6c188; }
.exam-archive .ea-prompt { font-family:var(--ef-serif); font-size:15px; line-height:1.55; color:var(--ef-ink); }
.exam-archive .ea-answer { font-size:13px; line-height:1.6; color:var(--ef-ink-soft); padding:8px 10px; background:var(--ef-note,#f3f1e7); border-radius:var(--ef-radius-sm); }
`;
  document.head.appendChild(style);
}
